import { Download } from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { FormTextField, GridCardContent } from "components/atoms";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { RegexHelper, TableHelper } from "helpers";
import { usePaginationRequest, useRequest } from "hooks/useRequest";
import { isEmpty } from "lodash";
import {
  IDownloadTrackingQRCodeRequest,
  IGetTrackingBarcodeRequest,
  IPaginateApiResponse,
  IPrintCartonRequest,
  ITrackingBarcode,
} from "model";
import { createContext, useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { DownloadActions, PrintActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { ReportApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import { ITrackingBarcodeInfo } from "../../../model/TrackingBarcode/ITrackingBarcodeInfo";

interface IQRCodeTrackingContext {
  onQRCodeDoubleClick?(code: string): void;
}
const QRCodeTrackingContext = createContext<IQRCodeTrackingContext>({});
export const useQRCodeTrackingContext = () => {
  return useContext(QRCodeTrackingContext);
};

const validationSchema = yup.object().shape({
  barcode: yup.string().required(translations.yupError.required),
});

const BarcodeTracking = () => {
  const dispatch = useDispatch();
  const [request, setRequest] = useState<IGetTrackingBarcodeRequest>({
    barcode: "",
    type: "A",
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });

  const handleSubmit = (values: IGetTrackingBarcodeRequest) => {
    setRequest(values);
  };
  const handleChangePage = (pageNumber: number) => {
    setRequest({ ...request, pageNumber: pageNumber });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const { loading, result } = usePaginationRequest<
    IGetTrackingBarcodeRequest,
    ITrackingBarcode[]
  >(request, ReportApiService.getTrackingBarcode, [], {
    holdRequest: isEmpty(request.barcode),
  });

  const { loading: infoLoading, data: infoResult } = useRequest<
    IGetTrackingBarcodeRequest,
    ITrackingBarcodeInfo[]
  >(request, ReportApiService.getTrackingBarcodeInfo, [], {
    holdRequest: isEmpty(request.barcode),
  });

  const _downloadAuditTrail = () => {
    const params: IDownloadTrackingQRCodeRequest = {
      barCode: request.barcode,
      type: request.type,
      pageSize: (result as IPaginateApiResponse<ITrackingBarcode[]>)
        .totalRecords,
      pageNumber: FIRST_PAGE,
      fileName: `${EXPORT_FILE_NAME.qrCodeTracking}_[${request.barcode}]`,
    };
    dispatch(DownloadActions.downloadQRCodeTracking.request(params));
  };

  const value = useMemo(
    () => ({
      onQRCodeDoubleClick: (code: string) => {
        setRequest({
          ...request,
          type: getTrackingType(code),
          barcode: code,
        });
      },
    }),
    []
  );

  return (
    <QRCodeTrackingContext.Provider value={value}>
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
        }}
      >
        <DetailHeader
          request={request}
          onSubmit={handleSubmit}
          onDownloadTracking={_downloadAuditTrail}
        />
        <TableSection
          request={request}
          loading={loading}
          infoLoading={infoLoading}
          result={result}
          infoResult={infoResult}
          onChangePage={handleChangePage}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </QRCodeTrackingContext.Provider>
  );
};

export enum ETrackingBarcode {
  ALL = "A",
  PALLET = "P",
  CARTON = "C",
  PRODUCT = "E",
  PRODUCT_LOT = "L",
  LOCATION = "LC",
  INVENTORY = "M",
}

const BARCODE_TYPE = [
  {
    label: translations.type.allType,
    value: ETrackingBarcode.ALL,
  },
  {
    label: translations.type.pallet, //productPallet Code
    value: ETrackingBarcode.PALLET,
  },
  {
    label: translations.type.carton, //productCarton Code
    value: ETrackingBarcode.CARTON,
  },
  {
    label: translations.type.finishedProduct, //encrypted
    value: ETrackingBarcode.PRODUCT,
  },
  {
    label: translations.type.productLot, //productLot Code
    value: ETrackingBarcode.PRODUCT_LOT,
  },
  {
    label: translations.type.location, //productCode
    value: ETrackingBarcode.LOCATION,
  },
  {
    label: translations.type.inventory, //productCode
    value: ETrackingBarcode.INVENTORY,
  },
];

export const getTrackingType = (barcode: string) => {
  switch (true) {
    case RegexHelper.isCarton(barcode):
      return ETrackingBarcode.CARTON;
    case RegexHelper.isEncryptedBarcode(barcode):
      return ETrackingBarcode.PRODUCT;
    case RegexHelper.isPallet(barcode):
      return ETrackingBarcode.PALLET;
    case RegexHelper.isInventory(barcode): // Cant handle => fixing
      return ETrackingBarcode.INVENTORY;
    default:
      return ETrackingBarcode.PRODUCT_LOT;
  }
};

const DetailHeader = (props: {
  request: IGetTrackingBarcodeRequest;
  onSubmit(values: IGetTrackingBarcodeRequest): void;
  onDownloadTracking(): void;
}) => {
  const { request, onSubmit, onDownloadTracking } = props;
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const formik = useFormik<IGetTrackingBarcodeRequest>({
    initialValues: request,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (
      values: IGetTrackingBarcodeRequest,
      { validateForm }: FormikHelpers<any>
    ) => {
      validateForm().then(() => {
        const isAllType = values.type === ETrackingBarcode.ALL;
        if (isAllType) {
          const barcodeType = getTrackingType(values.barcode);
          const request = {
            ...values,
            type: barcodeType,
            pageNumber: FIRST_PAGE,
          };
          onSubmit(request);
        } else {
          const request = {
            ...values,
            type: values.type,
            pageNumber: FIRST_PAGE,
          };
          onSubmit(request);
        }
      });
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.trackingBarcode)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            loading={downloading}
            onClick={onDownloadTracking}
            variant="contained"
            startIcon={<Download />}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.report.code)}
            placeholder={I18nTranslator(translations.placeholder.code)}
            id="barcode"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.report.type)}
            id="type"
            select
          >
            {BARCODE_TYPE.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {I18nTranslator(type.label)}
              </MenuItem>
            ))}
          </FormTextField>
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const TableSection = (props: {
  loading: boolean;
  infoLoading: boolean;
  result: IPaginateApiResponse<ITrackingBarcode[]>;
  infoResult?: ITrackingBarcodeInfo[];
  request: IGetTrackingBarcodeRequest;
  onChangePage(pageNumber: number): void;
  onPageSizeChange(pageSize: number): void;
}) => {
  const {
    loading,
    infoLoading,
    result,
    infoResult,
    request,
    onChangePage,
    onPageSizeChange,
  } = props;
  const [headerTrackingTableHeight, setHeaderTrackingTableHeight] = useState(0);
  return (
    <GridCardContent sx={{ flexDirection: "row", display: "flex", flex: 1 }}>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          "& .tracking:hover": {
            textDecoration: "underline",
            color: "blue",
          },
        }}
      >
        <TrackingTable
          loading={loading}
          onChangePage={onChangePage}
          onPageSizeChange={onPageSizeChange}
          request={request}
          result={result}
          setHeaderHeight={setHeaderTrackingTableHeight}
        />
        <TrackingInfoTable
          loading={infoLoading}
          result={infoResult}
          headerTrackingTableHeight={headerTrackingTableHeight}
        />
      </Box>
    </GridCardContent>
  );
};

const TrackingTable = (props: {
  loading: boolean;
  result: IPaginateApiResponse<ITrackingBarcode[]>;
  request: IGetTrackingBarcodeRequest;
  onChangePage(pageNumber: number): void;
  setHeaderHeight(height: number): void;
  onPageSizeChange(pageSize: number): void;
}) => {
  const dispatch = useAppDispatch();
  const { onQRCodeDoubleClick } = useQRCodeTrackingContext();
  const { loading, request, result, onChangePage, onPageSizeChange } = props;

  const handleClickPrint = () => {
    const cartonBarcode = request.barcode;
    _printCartonBarcode({
      cartonBarcode,
    });
  };

  const _printCartonBarcode = (request: IPrintCartonRequest) => {
    dispatch(PrintActions.printCartonBarcode.request(request));
  };

  const handelQRCodeDoubleClick = (params: any) => {
    if (
      [
        "cartonBarcode",
        "palletCode",
        "locationCode",
        "productLot",
        "productCode",
      ].includes(params.field)
    ) {
      onQRCodeDoubleClick && onQRCodeDoubleClick(params.value);
    }
  };

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "locationCode",
      headerName: I18nTranslator(translations.report.locationCode),
      size: "small",
      align: "left",
      sortable: true,
      cellClassName: "tracking",
      renderCell: ({ value }) => {
        return (
          <Tooltip title="Nhấp đôi để truy vết vị trí này">
            <Typography>{value}</Typography>
          </Tooltip>
        );
      },
    }),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.report.palletCode),
      size: "small",
      align: "left",
      sortable: true,
      cellClassName: "tracking",
      renderCell: ({ value }) => {
        return (
          <Tooltip title="Nhấp đôi để truy vết pallet này">
            <Typography>{value}</Typography>
          </Tooltip>
        );
      },
    }),
    TableHelper.createCell({
      field: "cartonBarcode",
      headerName: I18nTranslator(translations.report.cartonCode),
      size: "small",
      cellClassName: "tracking",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => {
        return (
          <Tooltip title="Nhấp đôi để truy vết thùng này">
            <Typography>{value}</Typography>
          </Tooltip>
        );
      },
    }),
    TableHelper.createCell({
      field: "productBarcode",
      headerName: I18nTranslator(
        RegexHelper.isCarton(request.barcode)
          ? translations.report.productBarcode
          : translations.report.productBarcodeQuantity
      ),
      size: "medium",
      align: "right",
      renderCell: ({ row }) => {
        return <Typography>{row.productBarcode}</Typography>;
      },
    }),
    TableHelper.createCell({
      field: "encryptedProductBarcode",
      headerName: I18nTranslator(translations.report.encryptedProductBarcode),
      size: "full",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
      cellClassName: "tracking",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Tooltip title="Nhấp đôi để truy vết lô này">
            <Typography>{value}</Typography>
          </Tooltip>
        );
      },
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
      cellClassName: "tracking",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Tooltip title="Nhấp đôi để truy vết sản phẩm này">
            <Typography>{value}</Typography>
          </Tooltip>
        );
      },
    }),
    TableHelper.createCell({
      field: "productDescription",
      headerName: I18nTranslator(translations.report.productName),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productCodeOld",
      headerName: I18nTranslator(translations.report.productCodeOld),
      size: "small",
      align: "left",
    }),
  ];

  const disablePrintCarton =
    !RegexHelper.isCarton(request.barcode) || loading || isEmpty(result.data);
  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "row",
      }}
    >
      <AppTable<ITrackingBarcode>
        title="Thông tin chi tiết"
        loading={loading}
        data={result}
        columns={COLUMNS}
        onChangePage={onChangePage}
        onSearch={() => {}}
        onCellDoubleClick={handelQRCodeDoubleClick}
        bodyToolbarButton={
          <Stack direction={"row"} spacing={2}>
            <LoadingButton
              loading={false}
              variant="contained"
              onClick={handleClickPrint}
              startIcon={<PrintIcon />}
              disabled={disablePrintCarton}
            >
              {I18nTranslator(translations.button.printCarton)}
            </LoadingButton>
          </Stack>
        }
        hideHeader
        hideSearch
        hideImport
        hideAddNew
        hideDownload
        onHeaderHeightChanged={props.setHeaderHeight}
        removeActionColumn
        onPageSizeChange={onPageSizeChange}
      />
    </Box>
  );
};

const TrackingInfoTable = (props: {
  loading: boolean;
  result?: ITrackingBarcodeInfo[];
  headerTrackingTableHeight: number;
}) => {
  const { loading, result, headerTrackingTableHeight } = props;

  const COLUMNS2: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "infoName",
      headerName: I18nTranslator(translations.report.information),
      size: "small",
    }),
    TableHelper.createCell({
      field: "infoValue",
      headerName: I18nTranslator(translations.report.value),
      size: "medium",
    }),
  ];

  return (
    <Box
      sx={{
        width: 500,
        display: "flex",
        position: "relative",
      }}
    >
      <Typography
        variant="h2"
        sx={{
          position: "absolute",
          top: "16px",
          left: "16px",
          zIndex: 2,
        }}
      >
        {I18nTranslator(translations.tracking.generaInformation)}
      </Typography>
      <Box
        sx={{
          width: 400,
          display: "flex",
          flexDirection: "row",
          marginLeft: "8px",
          marginTop: `${headerTrackingTableHeight}px`,
        }}
      >
        <DataGrid
          loading={loading}
          rows={
            result
              ? result.map((item, index) => {
                  return {
                    ...item,
                    id: index,
                  };
                })
              : []
          }
          columns={COLUMNS2}
          rowHeight={40}
          disableSelectionOnClick
          hideFooter
          hideFooterPagination
          hideFooterSelectedRowCount
          style={{
            backgroundColor: "white",
            border: 0,
            boxShadow:
              "0px 9px 16px rgb(159 162 191 / 18%), 0px 2px 2px rgb(159 162 191 / 32%)",
          }}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {I18nTranslator(translations.table.noData)}
              </Stack>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default BarcodeTracking;
